/**
 * WareBee Digital Twin - Today/Operations KPIs
 * Real-time operational metrics for the current day
 */

import { ActivityEventProcessType } from '@warebee/shared/import-converter';

/**
 * Today Operations Mock Data
 */
export const todayMockData = {
  openOrdersByQueue: [
    { queue: 'Priority', count: 23 },
    { queue: 'Standard', count: 45 },
    { queue: 'Expedited', count: 12 },
    { queue: 'Returns', count: 8 },
    { queue: 'Special Handling', count: 5 },
  ],
  confirmedOrdersByQueue: [
    { queue: 'Priority', count: 42 },
    { queue: 'Standard', count: 78 },
    { queue: 'Expedited', count: 25 },
    { queue: 'Returns', count: 14 },
    { queue: 'Special Handling', count: 9 },
  ],
  openPickTasksByArea: [
    { area: 'Zone A', count: 18 },
    { area: 'Zone B', count: 24 },
    { area: 'Zone C', count: 12 },
    { area: 'Bulk Storage', count: 7 },
    { area: 'Special Items', count: 5 },
  ],
  openPutawayTasksByArea: [
    { area: 'Receiving', count: 15 },
    { area: 'Zone A', count: 9 },
    { area: 'Zone B', count: 12 },
    { area: 'Bulk Storage', count: 20 },
    { area: 'High Rack', count: 8 },
  ],
  openTasksByArea: [
    { area: 'Zone A', count: 32 },
    { area: 'Zone B', count: 41 },
    { area: 'Zone C', count: 23 },
    { area: 'Receiving', count: 18 },
    { area: 'Shipping', count: 27 },
    { area: 'Bulk Storage', count: 36 },
  ],
  openTasksByOverdueHours: [
    { hours: '0-1', count: 12 },
    { hours: '1-2', count: 8 },
    { hours: '2-4', count: 5 },
    { hours: '4-8', count: 3 },
    { hours: '8+', count: 2 },
  ],
  openTasksByCategory: [
    { category: 'Inbound', count: 35 },
    { category: 'Storage', count: 42 },
    { category: 'Outbound', count: 53 },
    { category: 'Value-Add', count: 12 },
    { category: 'Inventory', count: 28 },
  ],
  openTasksByType: [
    { type: 'Pick', count: 45 },
    { type: 'Putaway', count: 32 },
    { type: 'Pack', count: 18 },
    { type: 'Move', count: 15 },
    { type: 'Count', count: 12 },
    { type: 'QC', count: 8 },
    { type: 'Load', count: 22 },
  ],
};

/**
 * Today/Operations KPIs
 */
export const todayKpis /*: WarehouseKPI[] */ = [
  {
    id: 'open_warehouse_orders_by_queue',
    category: 'today',
    title: 'Open Warehouse Orders by Queue',
    description: 'Number of open warehouse orders by processing queue',
    unit: 'orders',
    formula: 'COUNT(orders WHERE status = "open") GROUP BY queue',
    tags: ['operations', 'today', 'orders'],
    benchmark: {
      standards: {
        site: {
          target: 10,
          warning: { upper: 25 },
          critical: { upper: 50 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 10 },
            { label: 'Warning', min: 10.01, max: 25 },
            { label: 'Critical', min: 25.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['queue', 'priority', 'order_type'],
    alertTopic: 'warehouse.alerts.today.open_orders_queue',
    processType: [
      ActivityEventProcessType.MANAGEMENT,
      ActivityEventProcessType.MANAGEMENT_WMS,
    ],
  },
  {
    id: 'confirmed_warehouse_orders_by_queue',
    category: 'today',
    title: 'Confirmed Warehouse Orders by Queue',
    description: 'Number of confirmed warehouse orders by processing queue',
    unit: 'orders',
    formula: 'COUNT(orders WHERE status = "confirmed") GROUP BY queue',
    tags: ['operations', 'today', 'orders'],
    benchmark: {
      standards: {
        site: {
          target: 50,
          warning: { lower: 30 },
          critical: { lower: 15 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 30 },
            { label: 'Target', min: 30.01, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['queue', 'priority', 'order_type'],
    alertTopic: 'warehouse.alerts.today.confirmed_orders_queue',
    processType: [
      ActivityEventProcessType.MANAGEMENT,
      ActivityEventProcessType.MANAGEMENT_WMS,
    ],
  },
  {
    id: 'open_pick_tasks_by_area',
    category: 'today',
    title: 'Open Pick Tasks by Activity Area',
    description: 'Number of open pick warehouse tasks by activity area',
    unit: 'tasks',
    formula:
      'COUNT(tasks WHERE type = "pick" AND status = "open") GROUP BY activity_area',
    tags: ['operations', 'today', 'tasks', 'picking'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { upper: 30 },
          critical: { upper: 50 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 30 },
            { label: 'Critical', min: 30.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['activity_area', 'priority', 'user_group'],
    alertTopic: 'warehouse.alerts.today.open_pick_tasks_area',
    processType: [
      ActivityEventProcessType.PICKING,
      ActivityEventProcessType.MANAGEMENT_WMS,
    ],
  },
  {
    id: 'open_putaway_tasks_by_area',
    category: 'today',
    title: 'Open Putaway Tasks by Activity Area',
    description: 'Number of open putaway warehouse tasks by activity area',
    unit: 'tasks',
    formula:
      'COUNT(tasks WHERE type = "putaway" AND status = "open") GROUP BY activity_area',
    tags: ['operations', 'today', 'tasks', 'putaway'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { upper: 30 },
          critical: { upper: 50 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 30 },
            { label: 'Critical', min: 30.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['activity_area', 'priority', 'user_group'],
    alertTopic: 'warehouse.alerts.today.open_putaway_tasks_area',
    processType: [
      ActivityEventProcessType.PUTAWAY,
      ActivityEventProcessType.MANAGEMENT_WMS,
    ],
  },
  {
    id: 'open_tasks_by_area',
    category: 'today',
    title: 'Open Tasks by Activity Area',
    description: 'Number of open warehouse tasks by activity area',
    unit: 'tasks',
    formula: 'COUNT(tasks WHERE status = "open") GROUP BY activity_area',
    tags: ['operations', 'today', 'tasks'],
    benchmark: {
      standards: {
        site: {
          target: 30,
          warning: { upper: 60 },
          critical: { upper: 100 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 30 },
            { label: 'Warning', min: 30.01, max: 60 },
            { label: 'Critical', min: 60.01, max: 200 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['activity_area', 'priority', 'task_type'],
    alertTopic: 'warehouse.alerts.today.open_tasks_area',
    processType: [
      ActivityEventProcessType.MANAGEMENT,
      ActivityEventProcessType.MANAGEMENT_WMS,
    ],
  },
  {
    id: 'open_tasks_by_overdue_time',
    category: 'today',
    title: 'Open Tasks by Overdue Time',
    description: 'Number of open warehouse tasks by overdue time in hours',
    unit: 'tasks',
    formula:
      'COUNT(tasks WHERE status = "open" AND current_time > due_time) GROUP BY FLOOR((current_time - due_time)/3600)',
    tags: ['operations', 'today', 'tasks', 'overdue'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 5 },
          critical: { upper: 15 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0 },
            { label: 'Warning', min: 0.01, max: 5 },
            { label: 'Critical', min: 5.01, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['overdue_hours', 'task_type', 'priority'],
    alertTopic: 'warehouse.alerts.today.open_tasks_overdue',
    processType: [
      ActivityEventProcessType.MANAGEMENT,
      ActivityEventProcessType.MANAGEMENT_WMS,
    ],
  },
  {
    id: 'open_tasks_by_process_category',
    category: 'today',
    title: 'Open Tasks by Process Category',
    description: 'Number of open warehouse tasks by warehouse process category',
    unit: 'tasks',
    formula: 'COUNT(tasks WHERE status = "open") GROUP BY process_category',
    tags: ['operations', 'today', 'tasks', 'process'],
    benchmark: {
      standards: {
        site: {
          target: 20,
          warning: { upper: 40 },
          critical: { upper: 60 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 20 },
            { label: 'Warning', min: 20.01, max: 40 },
            { label: 'Critical', min: 40.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['process_category', 'priority', 'user_group'],
    alertTopic: 'warehouse.alerts.today.open_tasks_process_category',
    processType: [
      ActivityEventProcessType.MANAGEMENT,
      ActivityEventProcessType.MANAGEMENT_WMS,
    ],
  },
  {
    id: 'open_tasks_by_process_type',
    category: 'today',
    title: 'Open Tasks by Process Type',
    description: 'Number of open warehouse tasks by warehouse process type',
    unit: 'tasks',
    formula: 'COUNT(tasks WHERE status = "open") GROUP BY process_type',
    tags: ['operations', 'today', 'tasks', 'process'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { upper: 30 },
          critical: { upper: 50 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 30 },
            { label: 'Critical', min: 30.01, max: 80 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['process_type', 'priority', 'user_group'],
    alertTopic: 'warehouse.alerts.today.open_tasks_process_type',
    processType: [
      ActivityEventProcessType.MANAGEMENT,
      ActivityEventProcessType.MANAGEMENT_WMS,
    ],
  },
];

export default todayKpis;
